import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const FAQ_ITEMS = [
    {
      q: t('faq.items.0.q'),
      a: t('faq.items.0.a'),
    },
    {
      q: t('faq.items.1.q'),
      a: t('faq.items.1.a'),
    },
    {
      q: t('faq.items.2.q'),
      a: t('faq.items.2.a'),
    },
    {
      q: t('faq.items.3.q'),
      a: t('faq.items.3.a'),
    },
    {
      q: t('faq.items.4.q'),
      a: t('faq.items.4.a'),
    },
    {
      q: t('faq.items.5.q'),
      a: t('faq.items.5.a'),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getStyle = (idx: number): React.CSSProperties => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateX(0)" : "translateX(-20px)",
    transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
    transitionDelay: `${idx * 70}ms`
  });

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section id="faq" ref={sectionRef} className="py-20 md:py-32 bg-gradient-to-b from-[#f7fbff] to-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold text-[#13B6EC] bg-[#13B6EC]/8 tracking-widest uppercase mb-5">
            {t('faq.label')}
          </div>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {t('faq.title')}
          </h2>
          <p className="text-base text-gray-500" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            {t('faq.desc')}
          </p>
        </div>

        {/* FAQ List */}
        <div className="flex flex-col gap-3">
          {FAQ_ITEMS.map((item, idx) => (
            <div
              key={idx}
              className="faq-item"
              style={getStyle(idx)}
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full text-left glass-card-strong rounded-2xl px-6 py-5 flex items-start gap-4 group transition-all duration-300 cursor-pointer"
                style={{
                  boxShadow: openIndex === idx ? "0 4px 24px rgba(19,182,236,0.12)" : "0 2px 10px rgba(0,0,0,0.04)",
                  border: openIndex === idx ? "1.5px solid rgba(19,182,236,0.25)" : "1.5px solid transparent",
                }}
              >
                {/* Q Icon */}
                <div
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-xs font-black transition-colors duration-200"
                  style={{
                    background: openIndex === idx ? "#13B6EC" : "rgba(19,182,236,0.1)",
                    color: openIndex === idx ? "white" : "#13B6EC",
                  }}
                >
                  Q
                </div>

                {/* Question */}
                <div className="flex-1">
                  <span
                    className="font-bold text-gray-900 leading-relaxed text-sm md:text-base"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    {item.q}
                  </span>

                  {/* Answer */}
                  <div
                    className="overflow-hidden transition-all duration-400"
                    style={{
                      maxHeight: openIndex === idx ? "320px" : "0px",
                      marginTop: openIndex === idx ? "12px" : "0px",
                    }}
                  >
                    <div className="flex gap-3 pt-3 border-t border-gray-100">
                      <div
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-xs font-black"
                        style={{ background: "rgba(19,182,236,0.06)", color: "#13B6EC" }}
                      >
                        A
                      </div>
                      <p
                        className="text-sm text-gray-500 leading-relaxed pt-1"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Toggle Icon */}
                <div
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300"
                  style={{
                    background: "rgba(19,182,236,0.08)",
                    transform: openIndex === idx ? "rotate(45deg)" : "rotate(0deg)",
                  }}
                >
                  <i className="ri-add-line text-sm text-[#13B6EC]" />
                </div>
              </button>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400 mb-3" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            {t('faq.contact_desc')}
          </p>
          <a
            href="mailto:support@visipo.com"
            className="inline-flex items-center gap-2 text-[#13B6EC] text-sm font-semibold hover:underline cursor-pointer"
          >
            <i className="ri-mail-line" />
            {t('faq.contact_link')}
          </a>
        </div>
      </div>
    </section>
  );
}
